'use client';
import {
	appInfoRequestInterceptor,
	type AppSubType,
	type AppType,
	atomStore,
	getRelyingParty,
	isDev,
	Logger,
	queryClient,
	sessionAtom,
	useSettings,
	useWaitingRoomSignal,
	waitingRoomRequestInterceptor
} from '@capsicum/common';
import { SociosBlkSdk, type SociosBlkSdkOptions } from '@socios-blk/core';
import { useAtomValue } from 'jotai';
import { type PropsWithChildren, useEffect, useState } from 'react';

import { env } from '../../util/env';

/**
 * Initializes the Socios Blockchain SDK and syncs the authToken.
 * This provider needs to be used within QueryClientProvider and JotaiProvider.
 */
export const BlkSdkProvider = ({
	appHost,
	appSubType,
	appType,
	children,
	version
}: PropsWithChildren<{
	appHost?: string;
	appSubType: AppSubType;
	appType: AppType;
	version: string;
}>) => {
	const getWaitingRoomSignal = useWaitingRoomSignal();
	const [isLoading, setLoading] = useState(true);
	const { authToken } = useAtomValue(sessionAtom) ?? {};
	const {
		settings: { rpcUrl }
	} = useSettings();

	useEffect(() => {
		if (SociosBlkSdk.isInitialized()) {
			return;
		}

		const environment =
			isDev() && env.NEXT_PUBLIC_ENV === 'integration' ? 'local' : env.NEXT_PUBLIC_ENV || 'production';
		const relyingParty = getRelyingParty(environment);

		const options: SociosBlkSdkOptions = {
			appHost,
			authToken: authToken || '',
			env: environment,
			jotaiStore: atomStore,
			logger: Logger,
			queryClient,
			relyingParty,
			rpcUrl
		};

		SociosBlkSdk.init(options);

		const requestIntereceptor = [
			appInfoRequestInterceptor(version, appType, appSubType),
			waitingRoomRequestInterceptor(getWaitingRoomSignal)
		];

		SociosBlkSdk.addRequestInterceptor(requestIntereceptor);

		setLoading(false);
	}, []);

	useEffect(() => {
		if (!SociosBlkSdk.isInitialized() || !rpcUrl) {
			return;
		}

		SociosBlkSdk.setRpcUrl(rpcUrl);
	}, [rpcUrl]);

	useEffect(() => {
		if (!SociosBlkSdk.isInitialized() || !authToken) {
			return;
		}

		SociosBlkSdk.setAuthToken(authToken);
	}, [authToken]);

	return isLoading ? null : children;
};
